import React, { useEffect, useRef, useState } from 'react';
import { Heart, MessageCircle, Users, X } from 'lucide-react';
import { LiveViewersPayload, LiveChatMessage, ReactionCounts } from '../../services/liveService';
import { LiveChatPanel, LiveReactionsPanel, LiveViewersPanel } from './LiveRail';

export type MobileTab = 'chat' | 'reactions' | 'viewers' | null;

interface Props {
  active: MobileTab;
  onOpen: (tab: Exclude<MobileTab, null>) => void;
  onClose: () => void;
  onTab: (tab: Exclude<MobileTab, null>) => void;
  unread?: number;
  viewers?: LiveViewersPayload;
  chat: LiveChatMessage[];
  isAuthenticated: boolean;
  onSendChat: (text: string) => Promise<void> | void;
  reactionsCounts: ReactionCounts;
}

const compact = (n: number) =>
  n >= 1000 ? (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k' : n.toLocaleString('it-IT');

const LiveMobileDock: React.FC<Props> = ({
  active, onOpen, onClose, onTab, unread = 0, viewers,
  chat, isAuthenticated, onSendChat, reactionsCounts
}) => {
  const isOpen = active !== null;
  const tab = active || 'chat';
  const viewersCount = viewers?.count ?? 0;

  // Lampeggio contorno verde sull'icona chat ad ogni nuovo messaggio ricevuto
  // mentre il pannello chat NON e' aperto. Si spegne quando l'utente apre la chat.
  const [chatFlash, setChatFlash] = useState(false);
  const prevChatLen = useRef(chat.length);

  useEffect(() => {
    if (chat.length > prevChatLen.current && active !== 'chat') {
      setChatFlash(true);
    }
    prevChatLen.current = chat.length;
  }, [chat.length, active]);

  useEffect(() => {
    if (active === 'chat') setChatFlash(false);
  }, [active]);

  useEffect(() => {
    if (isOpen) {
      const orig = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = orig; };
    }
  }, [isOpen]);

  return (
    <>
      <div className="mobile-dock" role="tablist" aria-label="Dock diretta">
        <button
          type="button"
          className={active === 'reactions' ? 'is-active' : ''}
          onClick={() => onOpen('reactions')}
        >
          <Heart width={18} height={18} />
          Reagisci
        </button>
        <button
          type="button"
          className={(active === 'chat' ? 'is-active' : '') + (chatFlash && active !== 'chat' ? ' has-new' : '')}
          onClick={() => onOpen('chat')}
        >
          <MessageCircle width={18} height={18} />
          Chat
          {unread > 0 && active !== 'chat' && <span className="badge">{unread}</span>}
        </button>
        <button
          type="button"
          className={active === 'viewers' ? 'is-active' : ''}
          onClick={() => onOpen('viewers')}
        >
          <Users width={18} height={18} />
          {compact(viewersCount)} live
        </button>
      </div>

      <div
        className={'mobile-sheet-back' + (isOpen ? ' is-open' : '')}
        onClick={onClose}
        aria-hidden={!isOpen}
      />
      <div
        className={'mobile-sheet' + (isOpen ? ' is-open' : '')}
        role="dialog"
        aria-modal="true"
        aria-hidden={!isOpen}
      >
        <div className="mobile-sheet-handle" />
        <div className="mobile-sheet-head">
          <h3>
            {tab === 'chat' && (<><span className="dot" />Diretta · interagisci</>)}
            {tab === 'reactions' && 'Reazioni live'}
            {tab === 'viewers' && 'In ascolto ora'}
          </h3>
          <button
            type="button"
            className="mobile-sheet-close"
            onClick={onClose}
            aria-label="Chiudi"
          >
            <X width={18} height={18} />
          </button>
        </div>

        <div className="mobile-sheet-tabs" role="tablist">
          <button
            type="button"
            className={tab === 'chat' ? 'is-active' : ''}
            onClick={() => onTab('chat')}
          >Chat</button>
          <button
            type="button"
            className={tab === 'reactions' ? 'is-active' : ''}
            onClick={() => onTab('reactions')}
          >Reazioni</button>
          <button
            type="button"
            className={tab === 'viewers' ? 'is-active' : ''}
            onClick={() => onTab('viewers')}
          >In ascolto</button>
        </div>

        <div className={'mobile-sheet-body' + (tab === 'chat' ? ' is-chat' : '')}>
          {tab === 'chat' && <LiveChatPanel messages={chat} isAuthenticated={isAuthenticated} onSend={onSendChat} />}
          {tab === 'reactions' && <LiveReactionsPanel counts={reactionsCounts} />}
          {tab === 'viewers' && <LiveViewersPanel viewers={viewers} />}
        </div>
      </div>
    </>
  );
};

export default LiveMobileDock;
